import { Box, Drawer, useMediaQuery, styled } from "@mui/material";
import { canAccessPath, viewerFromAuth } from "../../enums/Navigation";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

// Icon imports
import { ReactComponent as CompaniesIcon } from "../../assets/images/companies.svg";
import { ReactComponent as DisconnectIcon } from "../../assets/images/disconnect.svg";
import { ReactComponent as DashboardIcon } from "../../assets/images/dashboard.svg";
import { ReactComponent as ExercisesIcon } from "../../assets/images/exercises.svg";
import { ReactComponent as TrainersIcon } from "../../assets/images/trainers.svg";
import { ReactComponent as SettingIcon } from "../../assets/images/setting.svg";
import { ReactComponent as TemplatesIcon } from "../../assets/images/templates.svg";
import { ReactComponent as ProgramRenewIcon } from "../../assets/images/program_renew.svg";
import { ReactComponent as NotificationsIcon } from "../../assets/images/notifications.svg";
import { ReactComponent as GuidesIcon } from "../../assets/images/guides.svg";
import { ReactComponent as FormsIcon } from "../../assets/images/forms.svg";
import { ReactComponent as ClientAlertsIcon } from "../../assets/images/client-alerts.svg";
import { ReactComponent as CouponsIcon } from "../../assets/images/coupons.svg";
import { ReactComponent as ClientsIcon } from "../../assets/images/clients.svg";
import { ReactComponent as TasksIcon } from "../../assets/images/tasks.svg";
import {
  FileUploadOutlined as ImportUsersIcon,
  FactCheck as CheckInIcon,
  BoltOutlined as AutomationsIcon,
  RocketLaunch as BuildConsoleIcon,
  RestaurantOutlined as FoodDbIcon,
  SpaOutlined as SupplementsIcon,
  StraightenOutlined as BodyMeasurementsIcon,
  MenuBookOutlined as MenuTemplatesIcon,
  BookmarkBorder as SavedMealsIcon,
  RestaurantMenuOutlined as RecipesIcon,
  StorefrontOutlined as MarketplaceIcon,
  HandshakeOutlined as PartnershipsIcon,
  HistoryEduOutlined as AuditLogIcon,
  SupervisorAccountOutlined as GroupAdminsIcon,
  ManageAccountsOutlined as ManagersIcon,
} from "@mui/icons-material";

import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import { getPendingPrograms } from "../../services/PlanSerice";
import { getPendingCheckInCount } from "../../services/CheckInService";
import { getMarketplaceStatus } from "../../services/PartnershipService";
import SideNavItem from "./SideNavItem";
import SideNavCategory from "./SideNavCategory";
import { layout } from "../../theme";
import { NUTRITION_V2_ENABLED } from "../nutrition/v2/featureFlag";

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "isSmallScreen",
})(({ theme, isSmallScreen }) => ({
  "& .MuiDrawer-paper": {
    width: layout.sideNavWidth,
    top: layout.envBannerHeight,
    height: `calc(100vh - ${layout.envBannerHeight}px)`,
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.sidebar,
    borderLeft: "none",
    borderRight: "none",
    borderInlineStart: `1px solid ${theme.palette.neutral[100]}`,
    boxShadow: "none",
    paddingTop: isSmallScreen ? 8 : layout.topBarHeight + 8,
    paddingInline: 8,
    paddingBottom: 16,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));

const NavigationContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  overflowX: "hidden",
  overflowY: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

const DisconnectSection = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  marginTop: 8,
  paddingTop: 12,
  borderTop: `1px solid ${theme.palette.neutral[100]}`,
}));

// WHO MAY SEE WHAT NO LONGER LIVES HERE. Every item's audience — role gates, the MANAGER
// capability gates, and the hard SYSTEM_ADMIN / COMPANY_GROUP_ADMIN allow-lists that used to sit in
// this file — is now declared once in `src/enums/Navigation.ts` and read below via canAccessPath().
// PermissionGuard reads the same registry when a route renders, so the nav and the URL bar can no
// longer disagree: previously the nav knew a screen wasn't yours while routing let you type your
// way in anyway.
//
// This is presentation, not enforcement — see the registry's header. The server decides.
//
// What DOES stay on an item is `enabled`: conditions that are not about permission at all (a
// feature flag, whether the marketplace is currently open). Those are runtime state, not audience.
const getNavCategories = (
  isSuperAdmin,
  penddingPrograms,
  pendingCheckInCount,
  location,
  marketplaceOpen
) => [
  {
    title: "ראשי",
    items: [
      {
        path: "/app/dashboard",
        text: "דשבורד",
        icon: <DashboardIcon />,
        active: location.pathname.includes("dashboard"),
      },
    ],
  },
  {
    title: "ניהול משתמשים",
    items: [
      {
        path: "/app/clients",
        text: "לקוחות",
        icon: <ClientsIcon />,
        active: location.pathname.includes("clients"),
      },
      {
        path: "/app/trainers",
        text: "מאמנים",
        icon: <TrainersIcon />,
        active: location.pathname.includes("trainers"),
      },
      {
        path: "/app/managers",
        text: "מנהלים",
        icon: <ManagersIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("managers"),
        newUntil: "2026-09-19",
      },
      {
        path: "/app/alerts",
        text: "הערות לקוחות",
        icon: <ClientAlertsIcon />,
        // Built but not offered to anyone yet — not a permission question, so it is switched off
        // here rather than described as an audience in the registry.
        enabled: false,
        active: location.pathname.includes("alerts"),
      },
      {
        path: "/app/notifications",
        text: "התראות",
        icon: <NotificationsIcon />,
        active: location.pathname.includes("notifications"),
        newUntil: "2025-12-26",
      },
      {
        path: "/app/tasks",
        text: "משימות",
        icon: <TasksIcon />,
        active: location.pathname.includes("tasks"),
        newUntil: "2026-01-03",
      },
      {
        path: "/app/check-ins",
        text: "צ'ק-אין",
        icon: <CheckInIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("check-ins"),
        showBadge: pendingCheckInCount > 0,
      },
      {
        path: "/app/import-users",
        text: "ייבוא משתמשים",
        icon: <ImportUsersIcon />,
        active: location.pathname.includes("import-users"),
        newUntil: "2026-01-02",
      },
    ],
  },
  {
    title: "תוכן ואימונים",
    items: [
      {
        path: "/app/templates",
        text: "תוכניות אימון",
        icon: <TemplatesIcon />,
        active:
          location.pathname.includes("/templates") &&
          !location.pathname.includes("global-templates"),
      },
      {
        path: "/app/program_renew",
        text: "עדכון תוכניות",
        icon: <ProgramRenewIcon />,
        active: location.pathname.includes("program_renew"),
        showBadge: penddingPrograms?.pendingPrograms?.length > 0,
      },
      {
        path: "/app/exercises",
        text: "תרגילים",
        icon: <ExercisesIcon />,
        active: location.pathname.includes("exercises"),
      },
      {
        path: "/app/import-template",
        text: "ייבוא תבנית",
        icon: <ImportUsersIcon />,
        active: location.pathname.includes("import-template"),
        newUntil: "2026-01-15",
      },
    ],
  },
  {
    // Nutrition is its own section, and every company-wide nutrition library in it is its own
    // page. They were mixed into "תזונה ומשאבים" with forms, guides and the marketplace, and two
    // of them (saved meals, menu templates) were tabs of one page behind a query string — so the
    // nav could not say which one you were on, and neither could the address bar.
    title: "תזונה",
    items: [
      {
        // The only food catalog. It serves a company admin their own foods plus the shared ones, and
        // a super-admin the whole platform's — one screen, scoped by who is asking.
        path: "/app/food-dictionary",
        text: "מאגר מזון",
        icon: <FoodDbIcon sx={{ fontSize: "20px" }} />,
        active:
          location.pathname === "/app/food-dictionary" ||
          location.pathname.startsWith("/app/food-dictionary/"),
      },
      {
        path: "/app/menu-templates",
        text: "תבניות תפריטים",
        icon: <MenuTemplatesIcon sx={{ fontSize: "20px" }} />,
        // A feature flag, not an audience — who may see it is declared in enums/Navigation.ts.
        enabled: NUTRITION_V2_ENABLED,
        // /app/global-templates redirects here, but a slow redirect should still light the row.
        active:
          location.pathname.startsWith("/app/menu-templates") ||
          location.pathname.includes("global-templates"),
      },
      {
        path: "/app/saved-meals",
        text: "ארוחות שמורות",
        icon: <SavedMealsIcon sx={{ fontSize: "20px" }} />,
        enabled: NUTRITION_V2_ENABLED,
        active:
          location.pathname.startsWith("/app/saved-meals") ||
          location.pathname.startsWith("/app/nutrition-library"),
      },
      {
        path: "/app/supplements",
        text: "מאגר תוספים",
        icon: <SupplementsIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("supplement"),
      },
      {
        path: "/app/recipes",
        text: "מתכונים",
        icon: <RecipesIcon sx={{ fontSize: "20px" }} />,
        // No `show: !isSuperAdmin` — the filter no longer reads `show`, and /app/recipes is absent
        // from SYSTEM_ADMIN_ALLOWED_PATHS, so canAccessPath already withholds it from a super-admin.
        active: location.pathname.includes("recipes"),
      },
    ],
  },
  {
    title: "משאבים",
    items: [
      {
        path: "/app/company-measurements",
        text: "מדדי גוף",
        icon: <BodyMeasurementsIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("company-measurements"),
        newUntil: "2026-08-12",
        // "תבניות תפריטים" used to sit here, pointing at /app/global-templates. That page is gone —
        // the V2 menu-templates page replaced it and the old path redirects there — so the item now
        // lives in the תזונה section above, beside the rest of the nutrition libraries.
      },
      {
        path: "/app/guides",
        text: "מדריכים",
        icon: <GuidesIcon />,
        active: location.pathname.includes("guides"),
      },
      {
        // "מתכונים" moved up into the תזונה section with the other food libraries.
        path: "/app/forms",
        text: "טפסים",
        icon: <FormsIcon />,
        active: location.pathname.includes("forms"),
      },
      {
        path: "/app/marketplace",
        text: "מרקטפלייס",
        icon: <MarketplaceIcon sx={{ fontSize: "20px" }} />,
        // WHO may see it is in the registry (system-admin / admin / trainer). WHETHER it is on
        // right now is runtime state: a SYSTEM_ADMIN always sees it (with a closed banner), a
        // company persona only while the marketplace is open.
        enabled: isSuperAdmin || marketplaceOpen,
        active: location.pathname === "/app/marketplace",
        newUntil: "2026-07-15",
      },
    ],
  },
  {
    title: "מערכת",
    items: [
      {
        path: "/app/partnerships",
        text: "ניהול שותפים",
        icon: <PartnershipsIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("partnerships"),
      },
      {
        path: "/app/coupons",
        text: "קופונים",
        icon: <CouponsIcon />,
        active: location.pathname.includes("coupons"),
      },
      {
        path: "/app/admin-messages",
        text: "הודעות מערכת",
        icon: <NotificationsIcon />,
        active: location.pathname.includes("admin-messages"),
      },
      {
        path: "/app/build-console",
        text: "בנייה ופרסום",
        icon: <BuildConsoleIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("build-console"),
      },
      {
        path: "/app/audit-log",
        text: "לוג ביקורת",
        icon: <AuditLogIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("audit-log"),
      },
      {
        path: "/app/companies",
        text: "חברות",
        icon: <CompaniesIcon />,
        active: location.pathname.includes("companies"),
      },
      {
        path: "/app/administrators",
        text: "מנהלי קבוצות",
        icon: <GroupAdminsIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("administrators"),
        newUntil: "2026-08-24",
      },
      {
        path: "/app/automations",
        text: "אוטומציות",
        icon: <AutomationsIcon sx={{ fontSize: "20px" }} />,
        active: location.pathname.includes("automations"),
        beta: true,
      },
      {
        path: "/app/setting",
        text: "הגדרות",
        icon: <SettingIcon />,
        active: location.pathname.includes("setting"),
      },
    ],
  },
];

const SideNav = ({ isMobileOpen, onMobileClose }) => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const auth = useAuth();
  const { isSuperAdmin, isCompanyGroupAdmin, logout } = auth;
  const { company } = useCompany();
  const location = useLocation();

  const [penddingPrograms, setPenddingPrograms] = useState({
    pendingPrograms: [],
  });
  const [pendingCheckInCount, setPendingCheckInCount] = useState(0);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);

  // Marketplace open/closed — controls visibility of the marketplace nav item.
  // Company-scoped (admin/marketplace/status); skip for super-admin, who always sees the
  // marketplace entry (it's in SYSTEM_ADMIN_ALLOWED_PATHS) and has no company context.
  useEffect(() => {
    if (isSuperAdmin() || isCompanyGroupAdmin()) return;
    getMarketplaceStatus()
      .then((res) => setMarketplaceOpen(!!res.data?.open))
      .catch(() => {});
  }, [location.pathname]);

  // Fetch pending programs + check-in count when route or company changes.
  // These are company-scoped badges — a SYSTEM_ADMIN has no company context, so we
  // skip them entirely (the underlying nav items are hidden for super-admin anyway,
  // and the /admin/* endpoints would 403/405 without a company).
  useEffect(() => {
    if (isSuperAdmin() || isCompanyGroupAdmin()) return;
    const companyId = company?.id || null;
    Promise.all([
      getPendingPrograms(companyId)
        .then((res) => setPenddingPrograms(res.data))
        .catch(() => {}),
      getPendingCheckInCount(companyId)
        .then((res) => setPendingCheckInCount(res.data?.count || 0))
        .catch(() => {}),
    ]);
  }, [location.pathname, company]);

  // Poll pending check-in count every 30 seconds (company-scoped — not for super-admin).
  useEffect(() => {
    if (isSuperAdmin() || isCompanyGroupAdmin()) return;
    const interval = setInterval(() => {
      const companyId = company?.id || null;
      getPendingCheckInCount(companyId)
        .then((res) => setPendingCheckInCount(res.data?.count || 0))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [company]);

  // The audience the navigation registry is resolved against: the roles this user holds plus their
  // delegated capability predicate. Built once per render and shared by every item's lookup.
  const viewer = viewerFromAuth(auth);

  const navCategories = getNavCategories(
    isSuperAdmin(),
    penddingPrograms,
    pendingCheckInCount,
    location,
    marketplaceOpen
  );

  return (
    <StyledDrawer
      anchor="right"
      onClose={onMobileClose}
      open={isSmallScreen ? isMobileOpen : true}
      variant={isSmallScreen ? "temporary" : "permanent"}
      isSmallScreen={isSmallScreen}
      ModalProps={{ keepMounted: true }}
      sx={{
        zIndex: isSmallScreen
          ? layout.zIndex.sideNavMobile
          : layout.zIndex.sideNav,
      }}
    >
      <NavigationContainer>
        {navCategories.map((category) => {
          const visibleItems = category.items.filter(
            (item) =>
              // Runtime state first (a flag being off is cheaper to answer than an audience), then
              // the single registry that routing consults too.
              item.enabled !== false && canAccessPath(item.path, viewer)
          );
          if (visibleItems.length === 0) return null;
          return (
            <SideNavCategory
              key={category.title}
              title={category.title}
              isMiniNav={false}
            >
              {visibleItems.map((item) => (
                <NavLink
                  key={item.path}
                  onClick={isSmallScreen ? onMobileClose : undefined}
                  to={item.path}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <SideNavItem
                    isMiniNav={false}
                    text={item.text}
                    active={item.active}
                    icon={item.icon}
                    showBadge={item.showBadge}
                    newUntil={item.newUntil}
                    comingSoon={item.comingSoon}
                    beta={item.beta}
                  />
                </NavLink>
              ))}
            </SideNavCategory>
          );
        })}
      </NavigationContainer>

      <DisconnectSection>
        <NavLink
          onClick={logout}
          to={"/app/login"}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <SideNavItem
            isMiniNav={false}
            text="התנתקות"
            active={false}
            icon={<DisconnectIcon />}
          />
        </NavLink>
      </DisconnectSection>
    </StyledDrawer>
  );
};

export default SideNav;
